import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Badge from 'atom-components/badge';
import { ResponseStatus } from 'services/assist-api/entities/test-instance-response/types';

/**
 *  Get bootstrap classnames to style the response status
 *
 *  - changes the background and text color (for good contrast)
 *  - capitalize the status
 */
const getResponseStatusClassNames = (status: ResponseStatus): string => {
  switch (status) {
    case ResponseStatus.Ready:
    case ResponseStatus.Started:
      return 'bg-warning text-dark text-capitalize';
    case ResponseStatus.Closed:
    case ResponseStatus.Finished:
      return 'bg-success text-light text-capitalize';
    default:
      return 'bg-info text-light text-capitalize';
  }
};

/**
 * Get fontawesome icons for the response status
 */
const getResponseStatusIcon = (status: ResponseStatus): ReactNode => {
  switch (status) {
    case ResponseStatus.Ready:
    case ResponseStatus.Started:
      return <FontAwesomeIcon icon="book-open" size="lg" />;
    case ResponseStatus.Closed:
    case ResponseStatus.Finished:
      return <FontAwesomeIcon icon="check-circle" size="lg" />;
    default:
      return <FontAwesomeIcon icon={['far', 'circle']} size="lg" />;
  }
};

/**
 * A Response Status Badge component
 *
 * This is a more specific implementation of our generic Badge.
 * Intended use is displaying response status
 * e.g: registered, ready, started, closed, finished
 * With some consistent styling
 */
export default function ResponseStatusBadge({
  children
}: {
  children: ResponseStatus;
}): ReactElement {
  return (
    <Badge pill className={getResponseStatusClassNames(children)}>
      <span className="me-2">{children}</span>
      {getResponseStatusIcon(children)}
    </Badge>
  );
}
